import React, { useState } from "react";
import axios from "axios";
import { useAuth } from "../../../contexts/AuthContext";
import { toast } from "react-toastify";
import ClosePositionForm from "./ClosePositionForm";
import EnableTradingForm from "./EnableTradingForm";
import DisableTradingForm from "./DisableTradingForm";
import ClosePositionOneAccount from "./ClosePositionOneAccount";
import GetConfigsForm from "./GetConfigsForm";
import StopTrackingLoginForm from "./StopTrackingLoginForm";
import DeleteTrackingLoginForm from "./DeleteTrackingLoginForm";
import UpdateConfigsForm from "./UpdateConfigsForm";
import GetPositionsTimelineForm from "./GetPositionsTimelineForm";
import GetBalanceEquity from "./GetBalanceEquity";
import GetMargin from "./GetMargin";
import GetFakeSystem from "./GetFakeSystem";
import SetFakeSystem from "./SetFakeSystem";

function AccountConfig() {
  const [activeTab, setActiveTab] = useState("configs");
  const [availableLogins, setAvailableLogins] = useState([]);
  const [availableRiskFields, setAvailableRiskFields] = useState([]);
  const [availablePipFields, setAvailablePipFields] = useState([]);
  const [loadingLogins, setLoadingLogins] = useState(false);
  const [targetType, setTargetType] = useState("individuals");
  const [showTargetModal, setShowTargetModal] = useState(true);
  const { apiBaseUrl } = useAuth();

  const fetchLogins = async (target) => {
    setLoadingLogins(true);
    try {
      const response = await axios.post(
        `${apiBaseUrl}/api/admin/final/accounts/configs`,
        {
          target: target,
        },
        {
          headers: {
            Accept: "application/json",
          },
        }
      );

      if (response.data.ok) {
        const loginConfigs = response.data.data?.data?.configurations;

        if (
          !loginConfigs ||
          loginConfigs === "" ||
          (Array.isArray(loginConfigs) && loginConfigs.length === 0)
        ) {
          setAvailableLogins([]);
          setAvailableRiskFields([]);
          setAvailablePipFields([]);
          return;
        }

        if (
          loginConfigs &&
          Array.isArray(loginConfigs) &&
          loginConfigs.length > 0
        ) {
          const logins = loginConfigs.map((config) => ({
            login: config.login,
            name: config.login_name || "",
            isActive: config.is_active === "true",
          }));
          setAvailableLogins(logins);

          const riskFieldsSet = new Set();
          const pipFieldsSet = new Set();

          loginConfigs.forEach((config) => {
            if (config.risk_config) {
              Object.keys(config.risk_config).forEach((key) => {
                riskFieldsSet.add(key);
              });
            }

            if (config.pip_config) {
              Object.keys(config.pip_config).forEach((key) => {
                pipFieldsSet.add(key);
              });
            }
          });

          const riskFields = Array.from(riskFieldsSet).sort();
          const pipFields = Array.from(pipFieldsSet).sort();

          setAvailableRiskFields(riskFields);
          setAvailablePipFields(pipFields);
        }
      }
    } catch (error) {
      console.error("Error fetching logins:", error);
      console.error("Error response:", error.response?.data);
    } finally {
      setLoadingLogins(false);
    }
  };

  const refetchConfigs = async () => {
    await fetchLogins(targetType);
  };

  const handleTargetSelection = (target) => {
    setTargetType(target);
    setShowTargetModal(false);
    fetchLogins(target);
  };

  const handleChangeTarget = () => {
    setShowTargetModal(true);
  };

  const tabs = [
    { id: "configs", label: "Get Configs", icon: "📋", color: "#3b82f6" },
    {
      id: "timeline",
      label: "Positions Timeline",
      icon: "📊",
      color: "#8b5cf6",
    },
    { id: "close", label: "Close Positions", icon: "🔒", color: "#ef4444" },
    { id: "enable", label: "Enable Trading", icon: "✅", color: "#10b981" },
    { id: "disable", label: "Disable Trading", icon: "🚫", color: "#f97316" },
    {
      id: "closeOne",
      label: "Close One Account",
      icon: "🎯",
      color: "#ec4899",
    },
    {
      id: "stopTracking",
      label: "Stop Tracking",
      icon: "⛔",
      color: "#dc2626",
    },
    {
      id: "deleteTracking",
      label: "Delete Tracking",
      icon: "🗑️",
      color: "#7c2d12",
    },
    {
      id: "getBalanceEquity",
      label: "Get Balance/Equity",
      icon: "💰",
      color: "#059669",
    },
    { id: "getmargin", label: "Get Margin", icon: "💰", color: "#059669" },
    {
      id: "getfakesystem",
      label: "Get Fake System",
      icon: "💰",
      color: "#059669",
    },
    {
      id: "setfakesystem",
      label: "Set Fake System",
      icon: "💰",
      color: "#059669",
    },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case "configs":
        return (
          <GetConfigsForm
            availableLogins={availableLogins}
            refetchConfigs={refetchConfigs}
          />
        );
      case "updateConfigs":
        return (
          <UpdateConfigsForm
            availableLogins={availableLogins}
            availableRiskFields={availableRiskFields}
            availablePipFields={availablePipFields}
            refetchConfigs={refetchConfigs}
          />
        );
      case "timeline":
        return (
          <GetPositionsTimelineForm
            availableLogins={availableLogins}
            refetchConfigs={refetchConfigs}
          />
        );
      case "close":
        return (
          <ClosePositionForm
            availableLogins={availableLogins}
            refetchConfigs={refetchConfigs}
          />
        );
      case "enable":
        return (
          <EnableTradingForm
            availableLogins={availableLogins}
            refetchConfigs={refetchConfigs}
          />
        );
      case "disable":
        return (
          <DisableTradingForm
            availableLogins={availableLogins}
            refetchConfigs={refetchConfigs}
          />
        );
      case "closeOne":
        return (
          <ClosePositionOneAccount
            availableLogins={availableLogins}
            refetchConfigs={refetchConfigs}
          />
        );
      case "stopTracking":
        return (
          <StopTrackingLoginForm
            availableLogins={availableLogins}
            refetchConfigs={refetchConfigs}
          />
        );
      case "deleteTracking":
        return (
          <DeleteTrackingLoginForm
            availableLogins={availableLogins}
            refetchConfigs={refetchConfigs}
          />
        );
      case "getBalanceEquity":
        return (
          <GetBalanceEquity
            availableLogins={availableLogins}
            refetchConfigs={refetchConfigs}
          />
        );
      case "getmargin":
        return (
          <GetMargin
            availableLogins={availableLogins}
            refetchConfigs={refetchConfigs}
          />
        );
      case "getfakesystem":
        return (
          <GetFakeSystem
            availableLogins={availableLogins}
            refetchConfigs={refetchConfigs}
          />
        );
      case "setfakesystem":
        return (
          <SetFakeSystem
            availableLogins={availableLogins}
            refetchConfigs={refetchConfigs}
          />
        );

      default:
        return (
          <GetConfigsForm
            availableLogins={availableLogins}
            refetchConfigs={refetchConfigs}
          />
        );
    }
  };

  const activeTabData = tabs.find((tab) => tab.id === activeTab);

  return (
    <div className="flex h-full" style={{ direction: "rtl" }}>
      {showTargetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-2xl p-8 max-w-md w-full mx-4 border border-gray-700 shadow-2xl">
            <h2 className="text-white text-2xl font-bold mb-4 text-center">
              انتخاب نوع حساب‌ها
            </h2>
            <p className="text-gray-400 text-center mb-8">
              لطفاً نوع حساب‌هایی که می‌خواهید مشاهده کنید را انتخاب کنید
            </p>

            <div className="space-y-4">
              <button
                onClick={() => handleTargetSelection("individuals")}
                className="w-full py-4 px-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition duration-200 flex items-center justify-center gap-3"
              >
                <span className="text-2xl">👤</span>
                <span>Individuals (حساب‌های فردی)</span>
              </button>

              <button
                onClick={() => handleTargetSelection("groups")}
                className="w-full py-4 px-6 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-xl transition duration-200 flex items-center justify-center gap-3"
              >
                <span className="text-2xl">👥</span>
                <span>Groups (گروه‌ها)</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <aside
        className="w-72 border-l border-gray-700 flex flex-col"
        style={{ backgroundColor: "#1f2937" }}
      >
        <div className="p-6 border-b border-gray-700">
          <h2 className="text-white text-xl font-bold mb-2">
            Account Management
          </h2>
          <p className="text-gray-400 text-sm">مدیریت حساب‌ها و تنظیمات</p>

          {!showTargetModal && (
            <div className="mt-4 p-3 bg-gray-800 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-lg">
                    {targetType === "individuals" ? "👤" : "👥"}
                  </span>
                  <span className="text-white text-sm font-medium">
                    {targetType === "individuals" ? "Individuals" : "Groups"}
                  </span>
                </div>
                <button
                  onClick={handleChangeTarget}
                  className="text-blue-400 hover:text-blue-300 text-xs underline"
                >
                  تغییر
                </button>
              </div>
            </div>
          )}
        </div>

        {loadingLogins && (
          <div className="px-4 py-3 mx-4 mt-4 bg-blue-900/30 border border-blue-600/50 text-blue-300 rounded-lg text-center text-xs">
            در حال بارگذاری لیست حساب‌ها...
          </div>
        )}

        <nav className="flex-1 p-4 overflow-y-auto">
          <div className="space-y-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                  activeTab === tab.id
                    ? "text-white shadow-lg transform scale-[1.02]"
                    : "text-gray-400 hover:text-white hover:bg-gray-800"
                }`}
                style={{
                  backgroundColor:
                    activeTab === tab.id ? tab.color : "transparent",
                }}
              >
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    activeTab === tab.id ? "bg-white/20" : "bg-gray-700"
                  }`}
                >
                  <span className="text-xl">{tab.icon}</span>
                </div>
                <span className="font-medium text-sm text-right">
                  {tab.label}
                </span>
              </button>
            ))}
          </div>
        </nav>

        <div className="p-4 border-t border-gray-700">
          <div className="bg-gray-800 rounded-lg p-3">
            <p className="text-gray-400 text-xs mb-1">تعداد حساب‌های موجود:</p>
            <p className="text-white font-bold text-lg">
              {availableLogins.length}
            </p>
          </div>
        </div>
      </aside>

      <main className="flex-1 flex flex-col p-8">
        <header
          className="border-b border-gray-700 shadow-sm"
          style={{ backgroundColor: "#1f2937" }}
        >
          <div className="px-8 py-5 flex items-center gap-4">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: activeTabData?.color || "#10b981" }}
            >
              <span className="text-2xl">{activeTabData?.icon}</span>
            </div>
            <div>
              <h3 className="text-white text-xl font-bold">
                {activeTabData?.label}
              </h3>
              <p className="text-gray-400 text-sm">
                مدیریت و کنترل {activeTabData?.label}
              </p>
            </div>
          </div>
        </header>

        <div
          className="flex-1 overflow-auto"
          style={{ backgroundColor: "#111827" }}
        >
          <div className="h-full">{renderContent()}</div>
        </div>
      </main>
    </div>
  );
}

export default AccountConfig;